import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Sprout, Leaf, TreePine, Lock, HeartHandshake, Users, Check, BookOpen, Sparkles, Timer, Play, Pause } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";
import MediaAttach from "./MediaAttach";
import VoiceRecorder from "./VoiceRecorder";
import VoicePlayer from "./VoicePlayer";
import { toast } from "sonner";

const ease = [0.22, 1, 0.36, 1];
function useMidnightCountdown() {
  const [ms, setMs] = useState(() => { const e = new Date(); e.setHours(24, 0, 0, 0); return e - new Date(); });
  useEffect(() => { const id = setInterval(() => { const e = new Date(); e.setHours(24, 0, 0, 0); setMs(e - new Date()); }, 1000); return () => clearInterval(id); }, []);
  const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${String(m).padStart(2, "0")}m left today`;
}
const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease } },
  exit: { opacity: 0, y: -18, transition: { duration: 0.4, ease } },
};

function Shell({ children, onExit, dark }) {
  return (
    <div data-testid="journey-flow" className={`fixed inset-0 z-50 overflow-y-auto ${dark ? "bg-[#211E1B] text-[#F4EFE6]" : "bg-[#F4EFE6] text-[#2A2825]"}`}>
      <div className="pointer-events-none fixed inset-0" style={{ background: dark ? "radial-gradient(ellipse at 50% 0%, rgba(169,114,78,0.14), transparent 60%)" : "radial-gradient(ellipse at 50% 0%, rgba(169,114,78,0.10), transparent 55%)" }} />
      <button onClick={onExit} data-testid="journey-exit" className={`fixed top-5 right-5 z-50 rounded-full p-2 transition-colors ${dark ? "text-[#C9BEB0] hover:bg-white/10" : "text-[#8a7a6a] hover:bg-black/5"}`}>
        <X className="w-5 h-5" strokeWidth={1.5} />
      </button>
      <div className="relative min-h-screen flex flex-col items-center px-6 py-24">
        <div className="m-auto w-full flex flex-col items-center">
          {children}
        </div>
      </div>
    </div>
  );
}

const Kicker = ({ children }) => (
  <p className="tracking-[0.25em] text-[11px] uppercase text-[#A9724E] mb-6">{children}</p>
);

const Primary = ({ children, onClick, testid, disabled }) => (
  <button onClick={onClick} data-testid={testid} disabled={disabled}
    className="group mt-10 inline-flex items-center gap-2.5 rounded-full bg-[#2A2825] text-[#F4EFE6] px-8 py-3.5 text-sm tracking-wide disabled:opacity-40 hover:bg-[#413d38] transition-colors">
    {children}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
  </button>
);

/* ---------------- Invitation ---------------- */
function Invitation({ day, onContinue }) {
  const c = day.content || {};
  return (
    <motion.div key="invitation" {...fade} className="max-w-xl text-center">
      <Kicker>Week {day.week} · {day.theme || day.movement?.name}</Kicker>
      <h1 className="font-serif-display font-light text-4xl md:text-6xl leading-[1.05] mb-8">Today's Invitation</h1>
      <div className="space-y-6 text-[#5E4B3C]">
        <p className="font-serif-display text-2xl md:text-3xl italic leading-relaxed text-[#2A2825]">“{c.scripture_text || "There is nothing to finish today. Only an invitation to receive."}”</p>
        {c.scripture_ref && <p className="tracking-widest text-xs uppercase text-[#A9724E]">{c.scripture_ref}</p>}
        {c.reflection_prompt && <p className="font-body text-base md:text-lg leading-relaxed max-w-md mx-auto">{c.reflection_prompt}</p>}
      </div>
      <Primary onClick={onContinue} testid="invitation-continue">Continue</Primary>
    </motion.div>
  );
}

/* ---------------- Choose Challenge ---------------- */
const TIER_ICON = { rooted: Sprout, growing: Leaf, outpouring: TreePine };
const TIER_TITLE = { rooted: "A gentle step", growing: "A little more", outpouring: "All the way" };
const TIER_BLURB = {
  rooted: "Smaller and quiet. Begin right where you are — no less holy for being simple.",
  growing: "Some courage required. You'll carry today's practice into a real moment with someone.",
  outpouring: "The deepest invitation. It may cost you something — and change you.",
};
function ChooseChallenge({ day, onChoose }) {
  const tooEarly = new Date().getHours() < 5;
  if (tooEarly) {
    return (
      <motion.div key="tooearly" {...fade} className="max-w-md text-center">
        <Kicker>Week {day.week} · {day.theme}</Kicker>
        <h1 className="font-serif-display font-light text-4xl mb-4">Rest a little longer.</h1>
        <p className="text-[#5E4B3C] font-body leading-relaxed">Today's challenge opens at 5:00 AM. There is nothing to strive for before then — sleep, pray, and come back when the day begins.</p>
      </motion.div>
    );
  }
  return (
    <motion.div key="choose" {...fade} className="max-w-4xl w-full text-center">
      <Kicker>Week {day.week} · {day.theme}</Kicker>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">Choose today's challenge</h1>
      <p className="text-[#5E4B3C] font-body mb-12">Choose only how far you'll go today. The challenge itself is a surprise — revealed once you say yes.</p>
      <div className="grid md:grid-cols-3 gap-5">
        {(day.challenge_options || []).map((opt, i) => {
          const Icon = TIER_ICON[opt.type] || Sprout;
          return (
            <motion.button key={opt.type} onClick={() => onChoose(opt.type)} data-testid={`challenge-${opt.type}`}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.15 + i * 0.12, duration: 0.6, ease } }}
              className="group text-left bg-white/70 border border-[#E0D8C9] rounded-2xl p-7 hover:border-[#A9724E] hover:-translate-y-1 transition-all duration-300">
              <Icon className="w-8 h-8 text-[#A9724E] mb-5" strokeWidth={1.25} />
              <p className="tracking-widest text-[10px] uppercase text-[#8a7a6a] mb-1">{opt.name}</p>
              <h2 className="font-serif-display text-2xl text-[#2A2825] mb-2 leading-snug">{TIER_TITLE[opt.type] || opt.name}</h2>
              <p className="text-sm text-[#5E4B3C] font-body leading-relaxed">{TIER_BLURB[opt.type]}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-xs text-[#A9724E]">Say yes to this <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" strokeWidth={1.5} /></span>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ---------------- Challenge Experience ---------------- */
function FocusTimer() {
  const [sec, setSec] = useState(300);
  const [run, setRun] = useState(false);
  useEffect(() => {
    if (!run || sec <= 0) return;
    const id = setInterval(() => setSec((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [run, sec]);
  const mm = String(Math.floor(sec / 60)).padStart(1, "0");
  const ss = String(sec % 60).padStart(2, "0");
  return (
    <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-5 py-2.5">
      <Timer className="w-4 h-4 text-[#E8B486]" strokeWidth={1.5} />
      <span className="font-serif-display text-xl tabular-nums">{mm}:{ss}</span>
      <button onClick={() => setRun((r) => !r)} data-testid="focus-timer-toggle" className="text-[#C9BEB0] hover:text-white transition-colors">
        {run ? <Pause className="w-4 h-4" strokeWidth={1.5} /> : <Play className="w-4 h-4" strokeWidth={1.5} />}
      </button>
    </div>
  );
}

function ChallengeExperience({ day, onComplete, busy, onWitness }) {
  const c = day.content || {};
  const opt = (day.challenge_options || []).find((o) => o.type === day.challenge_type) || {};
  const remaining = useMidnightCountdown();
  return (
    <motion.div key="challenge" {...fade} className="max-w-xl w-full text-center">
      <Kicker>Your challenge · {opt.name}</Kicker>
      <p className="text-[11px] tracking-widest uppercase text-[#E8B486]/80 -mt-4 mb-6" data-testid="challenge-countdown">{remaining}</p>
      <h1 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-4">{opt.title}</h1>
      <p className="text-[#C9BEB0] font-body leading-relaxed mb-10">{opt.body}</p>

      <div className="space-y-5 text-left">
        {c.prayer && (
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
            <p className="tracking-widest text-[10px] uppercase text-[#E8B486] mb-2">A prayer to carry</p>
            <p className="font-serif-display text-lg italic leading-relaxed text-[#F4EFE6]">{c.prayer}</p>
          </div>
        )}
        {c.scripture_text && (
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
            <p className="tracking-widest text-[10px] uppercase text-[#E8B486] mb-2 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} />{c.scripture_ref}</p>
            <p className="font-body leading-relaxed text-[#D9CFC2]">{c.scripture_text}</p>
          </div>
        )}
        {day.witness && (
          <button onClick={onWitness} data-testid="witness-card" className="group w-full text-left rounded-xl border border-white/10 bg-white/[0.04] p-6 flex items-center gap-4 hover:border-[#E8B486]/50 transition-colors">
            <div className="w-11 h-11 rounded-full bg-[#A9724E]/25 flex items-center justify-center text-[#E8B486]"><HeartHandshake className="w-5 h-5" strokeWidth={1.5} /></div>
            <div className="flex-1"><p className="tracking-widest text-[10px] uppercase text-[#E8B486]">This week's Witness</p><p className="font-serif-display text-lg">{day.witness.name}</p></div>
            <span className="text-[10px] text-[#C9BEB0] inline-flex items-center gap-1 group-hover:text-[#E8B486] transition-colors">View <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" strokeWidth={1.5} /></span>
          </button>
        )}
      </div>

      <div className="mt-10 flex flex-col items-center gap-6">
        <FocusTimer />
        <Primary onClick={onComplete} testid="challenge-complete" disabled={busy}>{busy ? "Saving…" : "Complete challenge"}</Primary>
      </div>
    </motion.div>
  );
}

/* ---------------- Wells Hub ---------------- */
const WELLS = [
  { key: "quiet", icon: Lock, title: "Quiet Well", tag: "Private · just you & God" },
  { key: "witness", icon: HeartHandshake, title: "Witness Well", tag: "Private · you & your Witness" },
  { key: "community", icon: Users, title: "Community Well", tag: "Shared · your whole home" },
];
function WellsHub({ day, onOpen }) {
  const done = day.wells || {};
  return (
    <motion.div key="wells" {...fade} className="max-w-lg w-full">
      <div className="text-center mb-10">
        <Kicker>The Reflection Journey</Kicker>
        <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">Three wells to draw from</h1>
        <p className="text-[#5E4B3C] font-body">Draw from each and you are <span className="italic text-[#A9724E]">fully witnessed</span>.</p>
      </div>
      <div className="space-y-4">
        {WELLS.map((w) => {
          const complete = !!done[w.key];
          return (
            <button key={w.key} onClick={() => !complete && onOpen(w.key)} data-testid={`well-${w.key}`} disabled={complete}
              className={`group w-full flex items-center gap-4 rounded-2xl border p-6 text-left transition-all duration-300 ${complete ? "border-[#A9724E]/40 bg-[#EAE3D9]/60" : "border-[#E0D8C9] bg-white/70 hover:border-[#A9724E] hover:-translate-y-0.5"}`}>
              <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${complete ? "bg-[#A9724E] text-[#F4EFE6]" : "bg-[#EAE3D9] text-[#A9724E]"}`}>
                {complete ? <Check className="w-5 h-5" strokeWidth={2} /> : <w.icon className="w-5 h-5" strokeWidth={1.5} />}
              </div>
              <div className="flex-1">
                <p className="tracking-widest text-[10px] uppercase text-[#8a7a6a]">{w.tag}</p>
                <h2 className="font-serif-display text-2xl text-[#2A2825]">{w.title}</h2>
              </div>
              {!complete && <ArrowRight className="w-5 h-5 text-[#A9724E] transition-transform group-hover:translate-x-1" strokeWidth={1.5} />}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ---------------- Single Well ---------------- */
const WELL_COPY = {
  quiet: { icon: Lock, title: "Quiet Well", prompt: "Just between you and God. No prompts, no assignments — write, record, or add a photo, however you need to.", scope: "private", placeholder: "Whatever you need to pour out…" },
  witness: { icon: HeartHandshake, title: "Witness Well", prompt: "Share honestly with the one walking beside you. To be seen is not to be judged.", scope: "private", placeholder: "A word for your Witness…" },
  community: { icon: Users, title: "Community Well", prompt: "Offer a story, a breakthrough, or a testimony to your home.", scope: "cohort", placeholder: "A story to share with your home…" },
};
function Well({ wellKey, day, onSubmit, onBack, busy }) {
  const cfg = WELL_COPY[wellKey];
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const Icon = cfg.icon;
  const voice = media.filter((m) => m.type === "audio");
  return (
    <motion.div key={`well-${wellKey}`} {...fade} className="max-w-xl w-full">
      <button onClick={onBack} className="text-sm text-[#8a7a6a] hover:text-[#A9724E] mb-6 inline-flex items-center gap-1.5">← Back to the wells</button>
      <div className="mb-6">
        <div className="w-12 h-12 rounded-full bg-[#EAE3D9] text-[#A9724E] flex items-center justify-center mb-4"><Icon className="w-5 h-5" strokeWidth={1.5} /></div>
        <Kicker>{wellKey === "witness" && day.witness ? `With ${day.witness.name}` : cfg.scope === "cohort" ? "Shared with your home" : "Private"}</Kicker>
        <h1 className="font-serif-display font-light text-3xl md:text-4xl mb-3">{cfg.title}</h1>
        <p className="text-[#5E4B3C] font-body leading-relaxed">{cfg.prompt}</p>
      </div>
      <textarea data-testid={`well-input-${wellKey}`} value={text} onChange={(e) => setText(e.target.value)} rows={6}
        placeholder={cfg.placeholder}
        className="w-full rounded-xl border border-[#E0D8C9] bg-white/70 p-4 font-body text-[#2A2825] placeholder:text-[#b3a695] focus:outline-none focus:border-[#A9724E] transition-colors resize-none" />
      <div className="mt-4 flex items-center gap-3">
        <MediaAttach value={media.filter((m) => m.type !== "audio")} onChange={(vis) => setMedia([...voice, ...vis])} scope={cfg.scope} testid={`well-media-${wellKey}`} />
        <div className="flex items-center gap-2">
          <VoiceRecorder testid={`well-voice-${wellKey}`} onRecorded={(p) => setMedia((m) => [...m, { type: "audio", path: p }])} />
          <span className="text-xs text-[#8a7a6a]">Record a voice reflection</span>
        </div>
      </div>
      {voice.length > 0 && <div className="mt-3">{voice.map((m, i) => <VoicePlayer key={i} path={m.path} />)}</div>}
      <Primary onClick={() => onSubmit({ text, media })} testid={`well-submit-${wellKey}`} disabled={busy || (!text.trim() && media.length === 0)}>
        {busy ? "Pouring out…" : "Draw from this well"}
      </Primary>
    </motion.div>
  );
}

/* ---------------- Completion ---------------- */
function Drops() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {Array.from({ length: 22 }).map((_, i) => (
        <motion.span key={i} initial={{ y: -40, opacity: 0 }}
          animate={{ y: "110vh", opacity: [0, 1, 1, 0] }}
          transition={{ duration: 3 + Math.random() * 2, delay: Math.random() * 1.5, repeat: Infinity, ease: "easeIn" }}
          className="absolute w-2 h-2 rounded-full bg-[#A9724E]/70"
          style={{ left: `${Math.random() * 100}%` }} />
      ))}
    </div>
  );
}
function Completion({ badges, onHome }) {
  return (
    <motion.div key="complete" {...fade} className="relative max-w-lg text-center">
      <Drops />
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1, transition: { duration: 0.8, ease } }}
        className="relative w-20 h-20 mx-auto mb-8 rounded-full bg-[#A9724E] text-[#F4EFE6] flex items-center justify-center">
        <Sparkles className="w-9 h-9" strokeWidth={1.25} />
      </motion.div>
      <Kicker>Today's journey complete</Kicker>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-4">You've been poured out.</h1>
      <p className="text-[#5E4B3C] font-body leading-relaxed">Held by God, by one, and by many. The garden grew a little today. Come back tomorrow.</p>
      {badges?.length > 0 && (
        <div className="mt-8 space-y-4 text-left">
          <p className="text-center tracking-overline text-[10px] uppercase text-[#A9724E]">A new badge{badges.length > 1 ? "s" : ""} — and what it means</p>
          {badges.map((b) => (
            <motion.div key={b.key} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1, transition: { delay: 0.4, type: "spring" } }}
              className="rounded-2xl bg-[#EAE3D9] border border-[#A9724E]/30 px-5 py-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#A9724E]" strokeWidth={1.5} />
                <span className="font-serif-display text-lg">{b.name}</span>
              </div>
              {b.scripture && <p className="text-sm text-[#5E4B3C] italic font-serif-display leading-relaxed mb-2">“{b.scripture}” <span className="not-italic text-[10px] tracking-overline uppercase text-[#A9724E]">— {b.scripture_ref}</span></p>}
              {b.walking && <p className="text-sm text-[#2A2825] font-body leading-relaxed">{b.walking}</p>}
            </motion.div>
          ))}
        </div>
      )}
      <Primary onClick={onHome} testid="journey-home">Return home</Primary>
    </motion.div>
  );
}


function MissedChallengeModal({ missed, onResolve, busy }) {
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("private");
  const when = (() => { try { return new Date(missed.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); } catch { return missed.date; } })();
  const options = [
    { key: "private", label: "Just for me", hint: "Kept private" },
    ...(missed.has_witness ? [{ key: "witness", label: "My Witness", hint: "Only they'll see it" }] : []),
    { key: "public", label: "The Community", hint: "Shared in the well" },
  ];
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="missed-challenge-modal">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-lg p-6 md:p-8 shadow-2xl max-h-[92vh] overflow-y-auto">
        <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-2">A challenge slipped by</p>
        <h3 className="font-serif-display font-light text-3xl mb-2">You missed {when}.</h3>
        <p className="text-[#5E4B3C] font-body leading-relaxed mb-4">
          That's okay — this isn't about a broken streak. It's about honesty. The <span className="italic">{missed.challenge_name || "invitation"}</span> was:
        </p>
        {missed.challenge_title && (
          <div className="bg-[#EAE3D9]/70 rounded-lg px-4 py-3 mb-5">
            <p className="font-serif-display text-[#2A2825] mb-1">{missed.challenge_title}</p>
            {missed.challenge_body && <p className="text-sm text-[#5E4B3C] font-body leading-relaxed">{missed.challenge_body}</p>}
          </div>
        )}
        <label className="block text-sm text-[#5E4B3C] font-body mb-1.5">What happened? Speak on it.</label>
        <textarea data-testid="missed-reflection-text" value={text} onChange={(e) => setText(e.target.value)} rows={4}
          placeholder="What got in the way — busyness, resistance, fear, forgetting? Name it honestly. God isn't disappointed; He's near."
          className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] resize-none mb-4" />
        <p className="text-xs tracking-overline uppercase text-[#8a7a6a] mb-2">Who can see this?</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {options.map((o) => (
            <button key={o.key} data-testid={`missed-visibility-${o.key}`} onClick={() => setVisibility(o.key)}
              className={`rounded-lg border px-3 py-2 text-left transition-colors ${visibility === o.key ? "border-[#A9724E] bg-[#EAE3D9]" : "border-[#D8CEBE] hover:border-[#c9b9a6]"}`}>
              <span className="block text-sm text-[#2A2825]">{o.label}</span>
              <span className="block text-[10px] text-[#8a7a6a] font-body">{o.hint}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <button data-testid="missed-save" onClick={() => onResolve(text, visibility)} disabled={busy || !text.trim()}
            className="w-full sm:w-auto rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-7 py-3 text-sm tracking-wide transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save & continue"}
          </button>
          <button data-testid="missed-skip" onClick={() => onResolve("", "private")} disabled={busy}
            className="w-full sm:w-auto text-sm text-[#8a7a6a] hover:text-[#A9724E] transition-colors">I'd rather not right now</button>
        </div>
      </motion.div>
    </div>
  );
}

/* ---------------- Controller ---------------- */
export default function JourneyFlow() {
  const navigate = useNavigate();
  const { user } = usePortalAuth();
  const [day, setDay] = useState(null);
  const [openWell, setOpenWell] = useState(null);
  const [busy, setBusy] = useState(false);
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    if (user && user.role !== "admin" && user.onboarded === false) navigate("/portal", { replace: true });
  }, [user, navigate]);

  const load = useCallback(() => portalApi.get("/portal/day").then((r) => setDay(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const call = async (fn) => { setBusy(true); try { const { data } = await fn(); setDay(data); return data; } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };

  const readInvitation = () => call(() => portalApi.post("/portal/day/invitation"));
  const chooseChallenge = (type) => call(() => portalApi.post("/portal/day/challenge", { type }));
  const completeChallenge = () => call(() => portalApi.post("/portal/day/challenge/complete", {}));
  const submitWell = async (payload) => {
    const data = await call(() => portalApi.post("/portal/day/well", { well: openWell, ...payload }));
    if (data?.celebration_badges?.length) setBadges(data.celebration_badges);
    setOpenWell(null);
  };

  const exit = () => navigate("/portal");

  const [savingMissed, setSavingMissed] = useState(false);
  const resolveMissed = async (text, visibility) => {
    setSavingMissed(true);
    try { await portalApi.post("/portal/day/missed/reflect", { date: day.missed.date, text, visibility }); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingMissed(false); }
  };

  if (!day) return <Shell onExit={exit}><p className="text-[#8a7a6a] font-body">Preparing today…</p></Shell>;

  if (!day.week) {
    return (
      <Shell onExit={exit}>
        <motion.div {...fade} className="max-w-md text-center">
          <Kicker>The journey</Kicker>
          <h1 className="font-serif-display font-light text-4xl mb-4">Your journey opens soon.</h1>
          <p className="text-[#5E4B3C] font-body">Rest here a moment. Your first invitation will arrive when your cohort begins.</p>
          <Primary onClick={exit} testid="journey-home">Return home</Primary>
        </motion.div>
      </Shell>
    );
  }

  const step = day.step;
  const dark = step === "challenge";

  return (
    <Shell onExit={exit} dark={dark}>
      <AnimatePresence mode="wait">
        {step === "invitation" && <Invitation day={day} onContinue={readInvitation} />}
        {step === "choose_challenge" && <ChooseChallenge day={day} onChoose={chooseChallenge} />}
        {step === "challenge" && <ChallengeExperience day={day} onComplete={completeChallenge} busy={busy} onWitness={() => day.witness && navigate(`/portal/profile/${day.witness.id}`)} />}
        {step === "reflection" && !openWell && <WellsHub day={day} onOpen={setOpenWell} />}
        {step === "reflection" && openWell && <Well wellKey={openWell} day={day} onSubmit={submitWell} onBack={() => setOpenWell(null)} busy={busy} />}
        {step === "complete" && <Completion badges={badges} onHome={exit} />}
      </AnimatePresence>
      {day.missed && <MissedChallengeModal missed={day.missed} onResolve={resolveMissed} busy={savingMissed} />}
    </Shell>
  );
}
